/*
 * index.js -- JEDINY vstupni bod JS bundlu sablony terrahunt-2026.
 *
 * Build: `npm run build:js`
 *   esbuild --bundle --target=es2018 --format=iife --global-name=TH2026
 *   -> assets/js/dist/th2026.js (+ .map)
 *
 * `--global-name=TH2026` znamena, ze esbuild priradi EXPORTY TOHOTO
 * SOUBORU do globalu `TH2026`. Bez exportu by `window.TH2026` zustalo
 * `undefined` a kontrakt A7 ("jeden namespace, zadne jine globaly") by
 * nebyl splneny -- proto tu exporty jsou i tehdy, kdyz je zatim nikdo
 * zvenci nevola: jsou to ty, ktere kontrakt slibuje externimu GTM
 * chatbot widgetu (docs/chatbot-position-contract.md).
 *
 * Exportuje se NAZVOVE i jako `default`: nazvove kvuli tomu, aby
 * `window.TH2026.setState(...)` fungovalo primo (u `export default` by
 * hodnota skoncila na `window.TH2026.default`), `default` kvuli pripadnym
 * budoucim importerum tohohle balicku.
 */

import { setState, hasState, states, contracts, enforceInvariant } from './state.js';
import * as viewport from './viewport.js';
import { init as initMenu } from './menu.js';
import { init as initSearchToggle } from './search-toggle.js';
import { init as initDock } from './dock.js';
import * as footer from './footer.js';
import { init as initReveal } from './reveal.js';
import { init as initHero } from './hero.js';
import { init as initHeroVideo, create as createHeroVideo } from './hero-video.js';
import { init as initCarousel } from './carousel.js';
import { init as initStats } from './stats.js';
import { init as initLightbox } from './lightbox.js';
import { init as initYoutubeFacade } from './youtube-facade.js';
import { init as initRoadmap } from './roadmap.js';
import { init as initLoksec } from './loksec.js';
import { init as initFormsec } from './formsec.js';
import { init as initZdrojNavstevy } from './zdroj-navstevy.js';
import { init as initGamepick } from './gamepick.js';
import { init as initHp2026 } from './hp2026.js';
import { init as initPartnerK13 } from './hp-partner-k13.js';
import { init as initFaqFilter } from './faq-filter.js';
import { init as initFaqChapterNav } from './faq-detail.js';
import { init as initBlogFilter } from './blog-filter.js';
import { init as initBlogLoadmore } from './blog-loadmore.js';
import { init as initRevmore } from './revmore.js';
import { init as initPulse } from './pulse.js';
import { init as initLazyPreload } from './lazy-preload.js';
import { init as initTouchAware } from './touch-aware.js';

/*
 * ---------------------------------------------------------------------
 * MODULY F0.5 -- inicializace
 * ---------------------------------------------------------------------
 * Sem (a nikam jinam) pridavaji dalsi vlny faze vlastni `import` nahore
 * a vlastni `init()` volani do bloku nize. Poradi je zamerne: `viewport`
 * musi zmerit referencni vysku DRIV, nez si na ni sáhne detekce
 * klavesnice v dock.js.
 *
 *   07-03  menu.js  -> import { init as initMenu } from './menu.js';
 *   BP     search-toggle.js -> import { init as initSearchToggle } from './search-toggle.js';
 *   07-04  dock.js  -> import { init as initDock } from './dock.js';
 *   07-05  footer.js -> import * as footer from './footer.js';
 *   08-02  reveal.js -> import { init as initReveal } from './reveal.js';
 *   08-07  hero.js  -> import { init as initHero } from './hero.js';
 *   CO3B   hero-video.js -> import { init as initHeroVideo, create as createHeroVideo } from './hero-video.js';
 *   08-08  carousel.js -> import { init as initCarousel } from './carousel.js';
 *   08-08  stats.js -> import { init as initStats } from './stats.js';
 *   08-09  lightbox.js -> import { init as initLightbox } from './lightbox.js';
 *   08-11  youtube-facade.js -> import { init as initYoutubeFacade } from './youtube-facade.js';
 *   08-12  roadmap.js -> import { init as initRoadmap } from './roadmap.js';
 *   11-03  loksec.js -> import { init as initLoksec } from './loksec.js';
 *   14-01  blog-filter.js -> import { init as initBlogFilter } from './blog-filter.js';
import { init as initRoadmap } from './roadmap.js';
 *   14-02  blog-loadmore.js -> import { init as initBlogLoadmore } from './blog-loadmore.js';
 *
 * Zadny z nich nesmi sahnout na `document.body.classList` primo -- stavy
 * prepina vyhradne `setState()` ze state.js (verejny kontrakt).
 *
 * KDY SE MODULY SPOUSTEJI: bundl je enqueue-nuty se `strategy: defer`
 * (`inc/assets.php`), takze prohlizec ho vykona az po dokonceni parsovani
 * dokumentu a PRED `DOMContentLoaded`. Pozadavek "init v DOMContentLoaded"
 * (07-07, bod 1) je tim splneny uz zpusobem nacteni -- vlastni posluchac by
 * inicializaci jen o jedno kolo smycky opozdil a prodlouzil okno, ve kterem
 * je hamburger jeste bez obsluhy.
 *
 * KAZDY init MA VLASTNI try/catch (07-07, bod 5). Tohle NENI defenzivni
 * ozdoba: bundl je jeden synchronni skript, takze neosetrena vyjimka
 * v prvnim volani zastavi vsechna nasledujici. Pad `menu.init()` by tedy
 * shodil i spodni listu a paticku -- tri rozbite veci misto jedne. Ctyri
 * SAMOSTATNE bloky (ne jeden spolecny) jsou proto podminka, ne styl; meri
 * to rezim `nobundle` v qa-matrix.cjs, varianta `throw`.
 * ---------------------------------------------------------------------
 */

/*
 * `touch-aware` (1.27.13, prulom majitele 14. 9.): no-op dotykove
 * posluchace na `document` -- viz docblok `touch-aware.js`. UPLNE PRVNI
 * init a NIKDY neodstranovat (brana `resizemut` kontroluje marker
 * `data-th-touch-aware`). Diagnosticky logger `tapdebug.js`, ktery
 * ten efekt objevil, byl v 1.27.16 odstranen.
 */
try {
	initTouchAware();
} catch (e) {
	console.error('TH2026 init selhal:', 'touch-aware', e);
}

try {
	viewport.init();
} catch (e) {
	console.error('TH2026 init selhal:', 'viewport', e);
}

try {
	initMenu();
} catch (e) {
	console.error('TH2026 init selhal:', 'menu', e);
}

try {
	initSearchToggle();
} catch (e) {
	console.error('TH2026 init selhal:', 'search-toggle', e);
}

try {
	initDock();
} catch (e) {
	console.error('TH2026 init selhal:', 'dock', e);
}

try {
	footer.init();
} catch (e) {
	console.error('TH2026 init selhal:', 'footer', e);
}

/*
 * `reveal` (08-02) je ZÁMĚRNĚ POSLEDNÍ a v samostatném bloku. Chrome
 * (hlavička, lišta, patička) musí být obsloužené dřív než entrance animace
 * obsahu: kdyby reveal spadl, nesmí to zdržet ani jednu z pojistek chrome.
 * Opačné pořadí by navíc posunulo start 1,2s fallbacku o dobu, kterou
 * spotřebují předchozí inity.
 */
try {
	initReveal();
} catch (e) {
	console.error('TH2026 init selhal:', 'reveal', e);
}

/*
 * `hero` (08-07) je az za `reveal` a ve vlastnim bloku ze stejneho duvodu:
 * vyber zdroje videa a tecky polaroidu jsou vylepseni jedne sekce, kdezto
 * chrome a reveal drzi pouzitelnost cele stranky. Kdyby hero spadlo, hero
 * zustane na posteru z `<picture>` a nic jineho se nezmeni.
 */
try {
	initHero();
} catch (e) {
	console.error('TH2026 init selhal:', 'hero', e);
}

/*
 * `hero-video` (CO3B) je hned za `hero` a ve vlastnim bloku ze stejneho
 * duvodu: hero video detailu hry je vylepseni jedne dlazdice galerie.
 * Kdyz modul spadne, zustane viditelny poster z markupu (`<video>` bez
 * `src` UZ v serverovem HTML je) a nic jineho se nezmeni -- na rozdil od
 * predchoziho stavu se ale ani nic zbytecne nestahne.
 */
try {
	initHeroVideo();
} catch (e) {
	console.error('TH2026 init selhal:', 'hero-video', e);
}

/*
 * `carousel` (08-08) je posledni ze stejneho duvodu jako `hero`: pas se bez
 * skriptu ovlada prstem uplne stejne (nativni `overflow-x` + scroll-snap),
 * takze pad tohohle modulu bere jen sipky, tecky a reposici po rotaci --
 * nic z obsahu. Vlastni blok proto, aby ho nezastavila vyjimka z hera.
 */
try {
	initCarousel();
} catch (e) {
	console.error('TH2026 init selhal:', 'carousel', e);
}

/*
 * `stats` (08-08) je uplne posledni: dopocitavani cisel je ciste ozdoba,
 * cilove hodnoty jsou v DOM uz ze sablony. Pad tohohle modulu proto
 * nezmeni na strance ani znak.
 */
try {
	initStats();
} catch (e) {
	console.error('TH2026 init selhal:', 'stats', e);
}

/*
 * `lightbox` (08-09) je za `stats` a ve vlastnim bloku ze stejneho duvodu
 * jako pas: galerie je i bez skriptu funkcni (nativne posuvny pas odkazu na
 * plne fotky), takze pad tohohle modulu bere jen modalni okno a pocitadlo.
 * Kdyz init neprobehne, `lightbox-ready` na <html> nepribude a pocitadlo
 * zustane skryte -- ukazatel pozice, ktery nikdo neaktualizuje, by lhal.
 */
try {
	initLightbox();
} catch (e) {
	console.error('TH2026 init selhal:', 'lightbox', e);
}

/*
 * `youtube-facade` (08-11) je za `lightbox` a ve vlastnim bloku ze stejneho
 * duvodu jako pas: facade je i bez skriptu funkcni (nahled + odkaz na
 * youtube.com/watch), takze pad tohohle modulu bere jen prehrani NA MISTE.
 * Divak se k videu dostane porad -- jen o jedno prekliknuti dal.
 */
try {
	initYoutubeFacade();
} catch (e) {
	console.error('TH2026 init selhal:', 'youtube-facade', e);
}

/*
 * `roadmap` (08-12) je uplne posledni a ve vlastnim bloku ze stejneho duvodu
 * jako pas: filtr stavu je vylepseni jednoho modulu, kdezto vsechno pred nim
 * drzi pouzitelnost cele stranky. Kdyz init neprobehne, chipy zustanou bez
 * obsluhy a seznam ukolu KOMPLETNI -- neztrati se ani radek obsahu.
 */
try {
	initRoadmap();
} catch (e) {
	console.error('TH2026 init selhal:', 'roadmap', e);
}

/*
 * `loksec` (11-03) je za `roadmap` a ve vlastnim bloku ze stejneho duvodu
 * jako pas: bublina nad piny mapy je vylepseni jedne sekce, kdezto vsechno
 * pred nim drzi pouzitelnost cele stranky. Kdyz init neprobehne, mapa
 * zustane statickym obrazkem a odkazovy seznam mest pod ni KOMPLETNI --
 * neztrati se ani jedna lokalita. Pod 1024 px se mapa nevykresluje vubec,
 * takze tam modul jen zjisti, ze neni co obsluhovat, a skonci.
 */
try {
	initLoksec();
} catch (e) {
	console.error('TH2026 init selhal:', 'loksec', e);
}

/*
 * `formsec` (rozhodnuti majitele 8. 9.) -- klientska kontrola poptavkoveho
 * formulare: povinne jmeno, e-mail a telefon, oznaceni vadneho pole
 * oranzovym rameckem s hlaskou a scroll na prvni z nich. Vlastni try/catch
 * jako u kazdeho jineho modulu: kdyby init spadl, formular se dal odesle
 * a rozhodne o nem SERVER (`terrahunt_inquiry_form_config()`), takze
 * povinna pole plati i bez tohohle radku.
 */
try {
	initFormsec();
} catch (e) {
	console.error('TH2026 init selhal:', 'formsec', e);
}

/*
 * `zdroj-navstevy` (26. 10. 2026): gclid/UTM/vstupní stránka do skrytých
 * polí poptávky (se souhlasem Konverzní i first touch 90 dní). Vlastní blok:
 * pád modulu nechá pole prázdná, odeslání formuláře nezastaví.
 */
try {
	initZdrojNavstevy();
} catch (e) {
	console.error('TH2026 init selhal:', 'zdroj-navstevy', e);
}

/*
 * `gamepick` (regrese nahlasena majitelem 8. 9.) -- stitkovy vicenasobny
 * vyber her a odkryvani souhlasu "rozsirit o outdoor aktivity" podle toho,
 * ktere hry maji volbu zapnutou v administraci. Stoji AZ ZA `formsec`,
 * protoze `formsec` si na formulari nastavuje `novalidate` a napojuje
 * submit listener; poradi mezi nimi je jinak nezavisle. Kdyby init spadl,
 * zustane v markupu nativni `<select multiple>` a formular funguje dal --
 * o outdoor volbe stejne rozhoduje SERVER (`class-form-handler.php`).
 */
try {
	initGamepick();
} catch (e) {
	console.error('TH2026 init selhal:', 'gamepick', e);
}

/*
 * `hp2026` (11-05b, kolo 3) -- pohyb homepage prenesenej 1:1 z mockupu:
 * vstupni animace hero, nastup sekcí, jedouci pasy, autoplay fotek,
 * count-up cisel, tooltip mapy a prehrani showreelu. Stoji AZ ZA
 * `loksec`, protoze cte skutecne rozmery pasu -- ty musi byt vysazene.
 * Mimo homepage modul rovnou skonci (nema koren `.th2026-hp-mockup`).
 */
try {
	initHp2026();
} catch (e) {
	console.error('TH2026 init selhal:', 'hp2026', e);
}

/*
 * `hp-partner-k13` (task AW3 KOREKCE) -- autoplay spoustec tri video
 * polaroidu v sekci Spoluprace (IntersectionObserver, jednou). Stoji
 * hned za `hp2026`, protoze cily na stejnem korenu `.th2026-hp-mockup`
 * a potrebuje hotovy DOM sekce Spoluprace.
 */
try {
	initPartnerK13();
} catch (e) {
	console.error('TH2026 init selhal:', 'hp-partner-k13', e);
}

/*
 * `faq-filter` (16-01) -- hledani a filtr publika na vypisu Napovedy. Bezi
 * hned, ne az na interakci: je to maly modul bez jedine externi zavislosti
 * a stranka ho potrebuje od prvniho stisku klavesy. Mimo Napovedu skonci
 * rovnou (v DOM neni ani jeden panel kategorie), takze jinde nic nestoji.
 * Kdyz init selze, zustane SSR seznam vsech clanku prolinkovany a uplny --
 * neztrati se ani jeden clanek, jen se neda filtrovat.
 */
if (document.querySelector('.fqwrap')) {
	try {
		initFaqFilter();
	} catch (e) {
		console.error('TH2026 init selhal:', 'faq-filter', e);
	}
}

/*
 * `faq-detail` (16-02) -- prehrani videonavodu od vybrane kapitoly. Bezi jen
 * na detailu Napovedy s kapitolami; jinde v DOM zadny `.chaplist` neni.
 * Kdyz init selze, zustanou kapitoly i tlacitko obycejnymi odkazy na YouTube.
 */
if (document.querySelector('.chaplist')) {
	try {
		initFaqChapterNav();
	} catch (e) {
		console.error('TH2026 init selhal:', 'faq-detail', e);
	}
}

/*
 * Filtr rubrik na výpisu blogu (14-01). Vlastní `try`/`catch` jako každý
 * jiný modul -- výjimka jedné komponenty nesmí shodit zbytek stránky.
 * Mimo blog modul rovnou skončí (nemá kořen `.bposts`).
 */
try {
	initBlogFilter();
} catch (e) {
	console.error('TH2026 init selhal:', 'blogFilter', e);
}

/*
 * AJAX „Načíst další" na výpisu blogu (14-02, TASK AY, nález majitele
 * 7. 9.). Vlastní `try`/`catch` jako každý jiný modul -- pád skriptu
 * nechá tlačítko fungovat jako fail-open funkční pagination bylo dřív
 * (D4), jen bez AJAXu. Mimo blog rovnou skončí (nemá `[data-loadmore]`).
 */
try {
	initBlogLoadmore();
} catch (e) {
	console.error('TH2026 init selhal:', 'blogLoadmore', e);
}

/*
 * "Cist vice" u dlouhych karet Google recenze na Reference (17-02, Owner
 * Decision Faze 17 #12). Mimo tuhle stranku rovnou skonci (nema `.revmore`).
 */
try {
	initRevmore();
} catch (e) {
	console.error('TH2026 init selhal:', 'revmore', e);
}

/*
 * `pulse` (task BD, majitel 7. 9.) -- sdíleny puls CTA/telefonu/mailu
 * (`.th-pulse`/`.th-pulse--glow`). Bezí sitewide (bez podminky na koren),
 * stejne jako `menu`/`dock`/`footer` -- na strankach bez tridy `.th-pulse`
 * `querySelectorAll` vrati prazdne pole a modul rovnou skonci. Vlastni
 * try/catch: pad animace nesmi shodit zbytek stranky.
 */
try {
	initPulse();
} catch (e) {
	console.error('TH2026 init selhal:', 'pulse', e);
}

/*
 * `footer` se vystavuje celý (ne jen `init`): fallbacková větev akordeonu
 * se na moderním enginu sama nikdy nespustí, takže by ji nešlo změřit.
 * Gate `_07-footer-accordion.cjs` si ji vyvolá přes
 * `TH2026.footer.setAllOpen(true)`.
 *
 * `reveal` se vystavuje jako objekt s `init` (ne holá funkce): modulový
 * gate `_08-reveal-module.cjs` se ptá na `TH2026.reveal.init` a stejný tvar
 * mají i budoucí komponentové moduly F1a.
 */
/*
 * LAZY-LOAD MAPY LOKALIT (12-01) -- jediná část výpisu Lokalit, která žije
 * v hlavním bundlu.
 *
 * PROČ TADY A NE V `map.js`: kdyby spouštěč seděl v mapovém modulu, musel
 * by se ten modul načíst, aby zjistil, že se nemá načítat. Hlavní bundl
 * proto drží jen ~15 řádků, které mapový bundl injektují AŽ po interakci
 * uživatele. OpenLayers tak stojí nulu, dokud mapu nikdo neotevře
 * (kontrakt „Pravidlo mapy", Success Criteria fáze: CLS mapy < 0,1).
 *
 * DVA SPOUŠTĚČE, PODLE ŠÍŘKY:
 *   - < 768 px: klik na `#tab-mapa`, a to OKAMŽITĚ. Mapa je ve skryté
 *     záložce; uživatel o ni výslovně požádal, takže zdržení by bylo jen
 *     čekáním na prázdnou plochu.
 *   - >= 768 px: `IntersectionObserver` na `.locmap`, ale injektáž se
 *     odloží za `load` a do nejbližší nečinné chvíle prohlížeče.
 *
 * PROČ NA DESKTOPU NESTAČÍ SAMOTNÝ POZOROVATEL (změřeno, ne odhadnuto).
 * Ve split view je mapa NAD OHYBEM: při výřezu 1440x900 začíná zhruba
 * na y = 340 px, takže pozorovatel se spustí hned při načtení a „lazy"
 * by neodložilo vůbec nic -- naměřeno v prvním běhu brány
 * `_12-01-lazy-map.mjs`, kde OpenLayers dorazil bez jediné interakce.
 * Cílem kontraktu ale není nutit uživatele scrollovat, nýbrž aby
 * OpenLayers nesoupeřil o pásmo a procesor s prvním vykreslením. Odklad
 * za `load` + `requestIdleCallback` to plní a mapu přitom dodá bez
 * jediného kliknutí.
 *
 * NA STRÁNCE BEZ MAPY SE NEINSTALUJE ANI POZOROVATEL. Výpis Her (12-02)
 * mapu nemá; bez téhle podmínky by na něm visel `IntersectionObserver`,
 * který nikdy nic neuvidí.
 */
function initLocmapLazy() {
	const locmap = document.querySelector( '.locmap' );

	if ( ! locmap ) {
		return;
	}

	const src = locmap.dataset.mapSrc || '';

	if ( ! src ) {
		// Bundl ještě neexistuje (fail-open) -- seznam funguje dál.
		return;
	}

	let loaded = false;

	function injectMapBundle() {
		if ( loaded ) {
			return;
		}

		loaded = true;

		const s = document.createElement( 'script' );
		s.src = src;
		s.defer = true;

		/*
		 * AŽ PO NAČTENÍ, NE POLLOVÁNÍM (12-03a, Task 3). Na mobilu se bundl
		 * injektuje v tomtéž kroku, ve kterém se odkrývá mapový panel, takže
		 * v okamžiku přepnutí ještě neexistuje. Rozměr se mu proto ohlásí
		 * podruhé tady -- navázané na jeho vlastní `load`, ne na časovač,
		 * který by běžel i tam, kde se bundl nikdy nenačte.
		 */
		s.addEventListener( 'load', function () {
			ohlasRozmer();
		}, { once: true } );

		document.body.appendChild( s );
	}

	/**
	 * Injektáž až ve chvíli, kdy prohlížeč nemá nic důležitějšího.
	 *
	 * @returns {void}
	 */
	function injectWhenIdle() {
		function schedule() {
			if ( typeof window.requestIdleCallback === 'function' ) {
				window.requestIdleCallback( injectMapBundle, { timeout: 2000 } );
				return;
			}

			window.setTimeout( injectMapBundle, 1200 );
		}

		if ( document.readyState === 'complete' ) {
			schedule();
			return;
		}

		window.addEventListener( 'load', schedule, { once: true } );
	}

	/*
	 * PŘEPÍNÁNÍ ZÁLOŽEK SEZNAM/MAPA (12-03a, Task 3) -- přenos 1:1
	 * z `navrh-th-mobil-lokality.html` ř. 2670-2690 (funkce `select()`).
	 *
	 * DO TOHOTO PLÁNU NEEXISTOVALO. Klik na `#tab-mapa` uměl JEDINOU věc:
	 * injektovat mapový bundl. Panel se nepřepnul, `aria-selected` zůstalo
	 * beze změny a OpenLayers se inicializoval nad kontejnerem, který byl
	 * pořád `hidden` -- tedy 0×0. Změřeno na DEV (390×844, obě mutace):
	 * po tapu na „Mapa" nedorazila ani jedna dlaždice OSM a synchronizace
	 * „jen ve výřezu" schovala VŠECH 30 (CZ) / 35 (EN) karet, `#cnt`
	 * ukazoval „0". Pro návštěvníka na telefonu to znamenalo prázdnou
	 * stránku po jediném ťuknutí.
	 *
	 * POŘADÍ KROKŮ JE PODSTATNÉ: nejdřív se přepne `hidden`, teprve pak se
	 * mapě říká „přepočítej rozměr". Obráceně by měřila ještě skrytý prvek.
	 *
	 * ROZMĚR SE HLÁSÍ DVAKRÁT, ne v cyklu: jednou hned (když už bundl běží
	 * z dřívějška) a jednou po jeho `load` (když se právě teď dotahuje).
	 * Pollování by běželo i tam, kde se bundl nikdy nenačte.
	 *
	 * POZICE SCROLLU SE PAMATUJE stejně jako v návrhu: mapa začíná nahoře,
	 * návrat na seznam vrací uživatele tam, kde přestal číst.
	 */
	const tabSeznam = document.getElementById( 'tab-seznam' );
	const tabMapa = document.getElementById( 'tab-mapa' );
	const paneSeznam = document.getElementById( 'seznam' );
	const paneMapa = document.getElementById( 'mapa' );

	/**
	 * Řekne mapovému modulu, že jeho kontejner změnil rozměr.
	 *
	 * Fail-open: dokud globální objekt bundlu neexistuje (ještě se načítá,
	 * nebo se načíst nepodařilo), funkce mlčí -- seznam funguje dál.
	 *
	 * @returns {void}
	 */
	function ohlasRozmer() {
		const modul = window.TH2026Map;

		if ( modul && typeof modul.updateSize === 'function' ) {
			modul.updateSize();
			return;
		}

		if ( modul && modul.default && typeof modul.default.updateSize === 'function' ) {
			modul.default.updateSize();
		}
	}

	/**
	 * BOD 8 (1.27.8, D2 diagnóza nález D): zavře kartu pinu v mapovém
	 * modulu (pokud běží a nějaká karta je otevřená) -- přepínač Mapa/
	 * Seznam o kartě pinu dřív vůbec nevěděl, protože modul mapy žádnou
	 * cestu ven na zavření nenabízel (`closePop` bylo jen interní).
	 *
	 * Fail-open: bez modulu (ještě se nenačetl -- mapa se injektuje
	 * líně) funkce mlčí, přepnutí panelu funguje dál beze změny.
	 *
	 * @returns {void}
	 */
	function zavriKartuPinu() {
		const modul = window.TH2026Map && window.TH2026Map.instance;

		if ( modul && typeof modul.closePop === 'function' ) {
			modul.closePop();
		}
	}

	if ( tabSeznam && tabMapa && paneSeznam && paneMapa ) {
		let scrollSeznamu = 0;

		/**
		 * Přepne na jeden ze dvou panelů.
		 *
		 * @param {boolean} naMapu `true` = panel Mapa, `false` = panel Seznam.
		 * @returns {void}
		 */
		function prepni( naMapu ) {
			if ( naMapu ) {
				scrollSeznamu = window.scrollY;
			}

			paneSeznam.hidden = naMapu;
			paneMapa.hidden = ! naMapu;
			tabSeznam.setAttribute( 'aria-selected', naMapu ? 'false' : 'true' );
			tabMapa.setAttribute( 'aria-selected', naMapu ? 'true' : 'false' );

			/*
			 * `body.map-mode` je CSS hook, který si pro F4 vyhradila
			 * `chrome.css` (ř. 1818, „ZÁMĚRNĚ BEZ PRAVIDLA"). Přepíná se
			 * tady, aby ho pravidla mohla použít, až vzniknou.
			 */
			document.body.classList.toggle( 'map-mode', naMapu );

			if ( naMapu ) {
				window.scrollTo( 0, 0 );
				injectMapBundle();
			} else {
				window.scrollTo( 0, scrollSeznamu );

				// BOD 8: přepnutí na Seznam zavře kartu pinu, pokud visí
				// nad mapou z předchozí návštěvy panelu Mapa.
				zavriKartuPinu();
			}

			ohlasRozmer();
		}

		tabSeznam.addEventListener( 'click', function () {
			prepni( false );
		} );

		tabMapa.addEventListener( 'click', function () {
			prepni( true );
		} );
	} else if ( tabMapa ) {
		// Mobil: uživatel o mapu výslovně požádal, žádné zdržení.
		tabMapa.addEventListener( 'click', injectMapBundle );
	}

	const desktop = window.matchMedia && window.matchMedia( '(min-width: 768px)' ).matches;

	if ( ! desktop ) {
		return;
	}

	if ( 'IntersectionObserver' in window ) {
		const io = new IntersectionObserver( function ( entries ) {
			entries.forEach( function ( entry ) {
				if ( entry.isIntersecting ) {
					io.unobserve( entry.target );
					injectWhenIdle();
				}
			} );
		}, { threshold: 0.1 } );

		io.observe( locmap );
		return;
	}

	// Bez pozorovatele (starý engine) se mapa načte taky, jen bez měření
	// viditelnosti -- degradace na dnešní chování webu, ne na chybějící mapu.
	injectWhenIdle();
}

try {
	initLocmapLazy();
} catch ( e ) {
	console.error( 'TH2026 init selhal:', 'locmapLazy', e );
}

/*
 * MAPA KANCELÁŘE NA KONTAKTU (17-01) -- druhý, nezávislý spouštěč lazy-loadu.
 *
 * DVĚ CESTY PODLE ŠÍŘKY, JEDNA SPOLEČNÁ INJEKTÁŽ:
 *   - OD 1024 px: `IntersectionObserver` nad `.cmap`. Mapa stojí nulu, dokud
 *     na ni uživatel nedojede; „interakce" je tady vstup do výřezu (tentýž
 *     výklad, jaký má `locmap.php` ve svém docbloku).
 *   - POD 1024 px: ŽÁDNÝ pozorovatel. Vidět je poster s tlačítkem a bundl se
 *     stáhne až po ťuknutí -- `docs/component-contract-mobile-2026.md` ř. 162
 *     to říká doslova („tap-to-activate ... žádné OL při loadu") a návrh
 *     mobilní verze se s ním shoduje.
 *
 * TŘI STAVY POSTERU (návrh ř. 2839-2887): idle (text z PHP) -> loading
 * (spinner + text z `data-cmap-loading-text`) -> mapa, NEBO chyba (text
 * z `data-cmap-error-text` + tlačítko `data-cmap-retry-text`, které pokus
 * zopakuje). Texty CHODÍ V `data-*`, v tomhle souboru není jediný český ani
 * anglický řetězec (rozhodnutí N8).
 *
 * NA STRÁNCE BEZ MAPY SE NEINSTALUJE NIC -- bez `.cmap` funkce hned skončí.
 */
function initContactMapLazy() {
	const cmap = document.querySelector( '.cmap' );

	if ( ! cmap ) {
		return;
	}

	const src = cmap.dataset.mapSrc || '';

	if ( ! src ) {
		// Bundl ještě neexistuje (fail-open) -- adresa i odkaz do navigace
		// jsou na stránce serverově, takže se nic neztratí.
		return;
	}

	const facade = cmap.querySelector( '[data-cmap-facade]' );
	let nacita = false;
	let hotovo = false;

	/**
	 * Řekne modulu mapy, že jeho kontejner změnil rozměr.
	 *
	 * @returns {void}
	 */
	function ohlasRozmer() {
		const modul = window.TH2026ContactMap;

		if ( modul && typeof modul.updateSize === 'function' ) {
			modul.updateSize();
			return;
		}

		if ( modul && modul.default && typeof modul.default.updateSize === 'function' ) {
			modul.default.updateSize();
		}
	}

	/**
	 * Vymění obsah posteru za chybový stav s tlačítkem „zkusit znovu".
	 *
	 * @returns {void}
	 */
	function chyba() {
		if ( ! facade ) {
			return;
		}

		while ( facade.firstChild ) {
			facade.removeChild( facade.firstChild );
		}

		const zprava = document.createElement( 'p' );
		zprava.setAttribute( 'role', 'status' );
		zprava.textContent = cmap.dataset.cmapErrorText || '';

		const znovu = document.createElement( 'button' );
		znovu.type = 'button';
		znovu.className = 'th-btn th-btn-primary';
		znovu.setAttribute( 'data-cmap-btn', '' );
		znovu.textContent = cmap.dataset.cmapRetryText || '';
		znovu.addEventListener( 'click', function () {
			nactiPoTapu();
		} );

		facade.appendChild( zprava );
		facade.appendChild( znovu );
		facade.hidden = false;
	}

	/**
	 * Injektuje bundl mapy. Volá se nejvýš jednou za úspěšný pokus.
	 *
	 * @param {Function} hotovoCb Zavolá se po načtení.
	 * @param {Function} chybaCb  Zavolá se při selhání.
	 * @returns {void}
	 */
	function injektuj( hotovoCb, chybaCb ) {
		if ( hotovo ) {
			hotovoCb();
			return;
		}

		if ( nacita ) {
			return;
		}

		nacita = true;

		const s = document.createElement( 'script' );
		s.src = src;
		s.defer = true;

		s.addEventListener( 'load', function () {
			nacita = false;
			hotovo = true;
			hotovoCb();
		}, { once: true } );

		s.addEventListener( 'error', function () {
			nacita = false;
			chybaCb();
		}, { once: true } );

		document.body.appendChild( s );
	}

	/**
	 * Cesta POD 1024 px: ťuknutí na tlačítko posteru.
	 *
	 * @returns {void}
	 */
	function nactiPoTapu() {
		if ( facade ) {
			while ( facade.firstChild ) {
				facade.removeChild( facade.firstChild );
			}

			const spinner = document.createElement( 'span' );
			spinner.className = 'msp';
			spinner.setAttribute( 'aria-hidden', 'true' );

			const zprava = document.createElement( 'p' );
			zprava.setAttribute( 'role', 'status' );
			zprava.textContent = cmap.dataset.cmapLoadingText || '';

			facade.appendChild( spinner );
			facade.appendChild( zprava );
		}

		injektuj( function () {
			if ( facade ) {
				facade.hidden = true;
			}

			// BOD 5 (1.27.8): `.olmap`/`.ol-attrib` jsou pod 1024 px skryté
			// (`components.css`, `.cmap:not(.mapactive)`), dokud mapa
			// neběží -- třída MUSÍ přibýt PŘED `ohlasRozmer()`, jinak
			// OpenLayers změří ještě skrytý (0×0) kontejner.
			cmap.classList.add( 'mapactive' );

			ohlasRozmer();
		}, chyba );
	}

	const desktop = window.matchMedia && window.matchMedia( '(min-width: 1024px)' ).matches;

	if ( ! desktop ) {
		cmap.addEventListener( 'click', function ( ev ) {
			const btn = ev.target.closest ? ev.target.closest( '[data-cmap-btn]' ) : null;

			if ( btn ) {
				nactiPoTapu();
			}
		} );

		return;
	}

	if ( 'IntersectionObserver' in window ) {
		const io = new IntersectionObserver( function ( entries ) {
			entries.forEach( function ( entry ) {
				if ( entry.isIntersecting ) {
					io.unobserve( entry.target );
					injektuj( ohlasRozmer, chyba );
				}
			} );
		}, { threshold: 0.1 } );

		io.observe( cmap );
		return;
	}

	// Bez pozorovatele (starý engine) se mapa načte taky, jen bez měření
	// viditelnosti -- degradace na dnešní chování webu, ne na chybějící mapu.
	injektuj( ohlasRozmer, chyba );
}

try {
	initContactMapLazy();
} catch ( e ) {
	console.error( 'TH2026 init selhal:', 'contactMapLazy', e );
}

/**
 * LAZY MAPA DETAILU HRY (13-01).
 *
 * Táž technika jako `initLocmapLazy()` výš, ale nad `.dmap` a s VLASTNÍM
 * příznakem `window.__th2026GameMapLoaded` -- kdyby obě mapy sdílely jednu
 * proměnnou, druhá stránka by si myslela, že už má bundl načtený.
 *
 * NA STRÁNCE BEZ `.dmap` SE NEINSTALUJE ANI POZOROVATEL: escape hry mapu
 * nemají vůbec a detail bez souřadnic ji fail-closed vynechá.
 *
 * @returns {void}
 */
function initGameMapLazy() {
	const dmap = document.querySelector('.dmap');

	if (!dmap) {
		return;
	}

	const src = dmap.dataset.mapSrc || '';

	if (!src) {
		// Bundl ještě neexistuje (fail-open) -- detail funguje dál bez mapy.
		return;
	}

	function injectGameMapBundle() {
		if (window.__th2026GameMapLoaded) {
			return;
		}

		window.__th2026GameMapLoaded = true;

		const s = document.createElement('script');

		s.src = src;
		s.defer = true;
		document.body.appendChild(s);
	}

	if ('IntersectionObserver' in window) {
		const io = new IntersectionObserver(function (entries) {
			entries.forEach(function (entry) {
				if (entry.isIntersecting) {
					io.unobserve(entry.target);
					injectGameMapBundle();
				}
			});
		}, { threshold: 0.1 });

		io.observe(dmap);
		return;
	}

	// Bez pozorovatele (starý engine) se mapa načte taky, jen bez měření.
	injectGameMapBundle();
}

try {
	initGameMapLazy();
} catch (e) {
	console.error('TH2026 init selhal:', 'gameMapLazy', e);
}

/**
 * GALERIE DETAILU HRY (13-01) -- filmstrip, mobilní pás a zvětšení fotky.
 *
 * PŘENOS CHOVÁNÍ 1:1 z návrhu (`navrh-th-detail-hra.html` ř. 1403-1430,
 * `navrh-th-mobil-detail.html` ř. 2429-2447): klik na náhled vymění hlavní
 * dlaždici, šipky posouvají pás, mobilní počítadlo se dopočítává z pozice
 * swipu a klik na hlavní fotku ji zvětší.
 *
 * BEZ NOVÉHO SINKU. Adresy fotek pocházejí z `data-full`/`src`, které
 * server vypsal přes `esc_url()`; JS je přesto před dosazením ověřuje
 * (root-relativní cesta nebo `http(s)`), takže ani podvržený atribut
 * neotevře `javascript:` cíl (pravidlo 39). Hlavní dlaždice se skládá
 * `createElement()`, ne `innerHTML` -- návrh používá `innerHTML`, ale to
 * je přesně ten vzor, který kontrakt zakazuje, a vizuální výsledek je
 * totožný.
 *
 * @returns {void}
 */
function initGameGallery() {
	const gal = document.querySelector('.gdet-gal');
	const mgal = document.querySelector('.mgal');

	if (!gal && !mgal) {
		return;
	}

	/**
	 * Bezpečný cíl obrázku, nebo prázdno.
	 *
	 * @param {string} url Hodnota z atributu.
	 * @returns {string} Adresa, nebo ''.
	 */
	function safeSrc(url) {
		const value = String(url || '');

		if (value.startsWith('/') && !value.startsWith('//')) {
			return value;
		}

		return /^https?:\/\//i.test(value) ? value : '';
	}

	/* --- Desktop: filmstrip a hlavní dlaždice --------------------------- */

	if (gal) {
		const strip = gal.querySelector('.dgthumbs');
		const main = gal.querySelector('.dgmain');
		const prev = gal.querySelector('.dgnav.prev');
		const next = gal.querySelector('.dgnav.next');

		if (strip && prev && next) {
			const stav = function () {
				const pretok = strip.scrollWidth - strip.clientWidth > 4;

				prev.classList.toggle('show', pretok && strip.scrollLeft > 4);
				next.classList.toggle('show', pretok && strip.scrollLeft < strip.scrollWidth - strip.clientWidth - 4);
			};

			const krok = function () {
				return Math.round(strip.clientWidth * 0.8);
			};

			prev.addEventListener('click', function () {
				strip.scrollBy({ left: -krok(), behavior: 'smooth' });
			});

			next.addEventListener('click', function () {
				strip.scrollBy({ left: krok(), behavior: 'smooth' });
			});

			strip.addEventListener('scroll', stav, { passive: true });

			if ('ResizeObserver' in window) {
				new ResizeObserver(stav).observe(strip);
			}

			stav();
		}

		if (strip && main) {
			const thumbs = Array.prototype.slice.call(strip.querySelectorAll('button'));

			/* Táž kontrola tvaru YouTube ID jako `youtube-embed.php`/`youtube-facade.js`. */
			const ID_RX = /^[A-Za-z0-9_-]{11}$/;

			thumbs.forEach(function (thumb) {
				thumb.addEventListener('click', function () {
					const ytId = thumb.getAttribute('data-video-yt') || '';
					const video = safeSrc(thumb.getAttribute('data-video'));
					const videoMobil = safeSrc(thumb.getAttribute('data-video-mobile'));
					const foto = safeSrc(thumb.getAttribute('data-full'));

					if (ytId && ID_RX.test(ytId)) {
						/*
						 * SPOLUPRÁCE (17-03 doplněk): video je YouTube, ne
						 * vlastní `.mp4` -- náhled se vrací do `.yt-facade`
						 * (STEJNÝ markup, jaký vypsal `youtube-embed.php`
						 * na serveru), ne do `<video>`. `youtube-facade.js`
						 * poslouchá kliky delegovaně na `document`, takže
						 * nově vložený uzel funguje beze změny tam.
						 */
						const poster = safeSrc(thumb.getAttribute('data-video-poster'));
						const label = thumb.getAttribute('data-video-label') || '';
						const wrap = document.createElement('div');
						const facade = document.createElement('div');
						const img = document.createElement('img');
						const link = document.createElement('a');
						const svgNS = 'http://www.w3.org/2000/svg';
						const svg = document.createElementNS(svgNS, 'svg');
						const path = document.createElementNS(svgNS, 'path');

						wrap.className = 'dgvideo';
						facade.className = 'yt-facade';
						facade.setAttribute('data-video-id', ytId);

						img.className = 'yt-facade-thumb';
						img.setAttribute('alt', label);
						img.loading = 'lazy';
						img.decoding = 'async';

						if (poster) {
							img.setAttribute('src', poster);
						} else {
							img.setAttribute('src', 'https://i.ytimg.com/vi/' + ytId + '/hqdefault.jpg');
							img.setAttribute('width', '480');
							img.setAttribute('height', '360');
						}

						link.className = 'yt-facade-play';
						link.setAttribute('href', 'https://www.youtube.com/watch?v=' + ytId);

						if (label) {
							link.setAttribute('aria-label', label);
						}

						svg.setAttribute('viewBox', '0 0 24 24');
						svg.setAttribute('aria-hidden', 'true');
						svg.setAttribute('focusable', 'false');
						path.setAttribute('d', 'M9 6.5v11l9-5.5z');
						path.setAttribute('fill', 'currentColor');
						svg.appendChild(path);
						link.appendChild(svg);

						facade.appendChild(img);
						facade.appendChild(link);
						wrap.appendChild(facade);
						main.textContent = '';
						main.appendChild(wrap);
					} else if (video) {
						/*
						 * CO3B: dlazdice se sklada SDILENOU TOVARNOU
						 * `hero-video.js`, ne rucne. Puvodni kod tu
						 * vyrabel `<video autoplay preload="metadata">`
						 * se `<source>` -- tedy presne ten tvar, ktery
						 * `game-hero-gallery.php` uz nevypisuje, protoze
						 * stahoval soubor i ve skryte variante. Dve
						 * ruzna mista skladajici tentyz prvek by se
						 * rozesla pri prvni dalsi zmene kontraktu.
						 */
						const nahled = thumb.querySelector('img');
						const el = createHeroVideo({
							srcDesktop: video,
							srcMobile: videoMobil,
							poster: safeSrc(thumb.getAttribute('data-video-poster')) || (nahled ? safeSrc(nahled.getAttribute('src')) : ''),
							label: thumb.getAttribute('aria-label') || '',
							role: 'desktop',
						});

						if (!el) {
							return;
						}

						const wrap = document.createElement('div');

						wrap.className = 'dgvideo';
						wrap.appendChild(el);
						main.textContent = '';
						main.appendChild(wrap);
					} else if (foto) {
						const img = document.createElement('img');
						const nahled = thumb.querySelector('img');

						img.setAttribute('src', foto);
						img.setAttribute('alt', nahled ? (nahled.getAttribute('alt') || '') : '');
						main.textContent = '';
						main.appendChild(img);
					} else {
						return;
					}

					thumbs.forEach(function (x) {
						x.classList.toggle('on', x === thumb);
					});
				});
			});
		}
	}

	/* --- Mobil: počítadlo a šipky nad swipe pásem ------------------------ */

	if (mgal) {
		const track = mgal.querySelector('.mgtrack');
		const counter = mgal.querySelector('.mgcount');
		const mprev = mgal.querySelector('.mgnav.prev');
		const mnext = mgal.querySelector('.mgnav.next');

		/*
		 * NÁLEZ MAJITELE 8. 9. (mobil, detail hry): „nezobrazí se jako první
		 * video, automaticky tam skočí např. až 5. fotka z galerie."
		 *
		 * PŘÍČINA JE V PROHLÍŽEČI, NE V POŘADÍ DAT. Pořadí pás dostává hotové
		 * ze serveru -- `game-hero-gallery.php` vypisuje video/banner jako
		 * první dlaždici a za ním fotky v pořadí z administrace
		 * (`th_api_game_gallery_items()`, CMB2 `file_list`). Změřeno na DEV
		 * (390x844, hasTouch): Chromium drží index 0 u 37/37 her, WEBKIT
		 * (Safari, tedy iPhone) skočí na náhodnou dlaždici -- 2, 4, 5, 7, 9,
		 * pokaždé jinou -- PŘESNĚ v okamžiku, kdy `.mgtrack` dostane první
		 * nenulový layout (`clientWidth` 0 -> 326). Je to re-snap
		 * `scroll-snap-type: x mandatory` (`components.css` ř. 9816) nad
		 * kontejnerem, který v předchozím layoutu neměl rozměr: WebKit si v tu
		 * chvíli vybere cíl snapu, a není to ten na `scrollLeft` 0. Žádný JS
		 * pás neposouvá -- instrumentace `scrollLeft`, `scrollTo`, `scrollBy`,
		 * `scrollIntoView` a `focus()` nad `.mgtrack` zaznamenala 0 volání.
		 *
		 * PROČ TAKHLE A NE V CSS. Vypnout snap úplně nález opraví, ale zabije
		 * snapování při swipu. Zapnout snap až z JS (CSS `none` -> třída
		 * s `x mandatory`) NEFUNGUJE: změřeno, přechod do `mandatory` spustí
		 * přesně týž náhodný re-snap (6/6 měřených her skočilo znovu). Drží
		 * jediné -- nechat snap zapnutý a po layoutu vrátit `scrollLeft` na 0.
		 *
		 * `ResizeObserver` je tu záměrně: jeho callback běží PO layoutu a PŘED
		 * vykreslením, takže se špatná dlaždice nestihne ukázat. `scroll`
		 * a `load` jsou záchytná síť pro pozdější přepočet (dotažený obrázek,
		 * překlopení písma).
		 *
		 * PO PRVNÍM DOTEKU UŽ SE NEKORIGUJE. Srovnávat pás během swipu by
		 * uživateli bralo listování, proto se `mgalInterakce` překlopí na
		 * první `touchstart`/`pointerdown`/`mousedown`/`wheel`/`keydown`
		 * kdekoli v `.mgal` -- tedy i na šipkách, které pásem posouvají samy.
		 */
		let mgalInterakce = false;

		if (track) {
			const zpetNaPrvni = function () {
				if (!mgalInterakce && 0 !== track.scrollLeft) {
					track.scrollLeft = 0;
				}
			};

			['touchstart', 'pointerdown', 'mousedown', 'wheel', 'keydown'].forEach(function (udalost) {
				mgal.addEventListener(udalost, function () {
					mgalInterakce = true;
				}, { passive: true });
			});

			zpetNaPrvni();

			if ('ResizeObserver' in window) {
				new ResizeObserver(zpetNaPrvni).observe(track);
			}

			track.addEventListener('scroll', zpetNaPrvni, { passive: true });
			window.addEventListener('load', zpetNaPrvni);
		}

		if (track && counter) {
			const total = track.children.length;
			let tick = null;

			const stav = function () {
				const i = track.clientWidth > 0 ? Math.round(track.scrollLeft / track.clientWidth) : 0;

				counter.textContent = (i + 1) + '/' + total;

				if (mprev) {
					mprev.hidden = i <= 0;
				}

				if (mnext) {
					mnext.hidden = i >= total - 1;
				}
			};

			track.addEventListener('scroll', function () {
				window.clearTimeout(tick);
				tick = window.setTimeout(stav, 70);
			}, { passive: true });

			if (mprev) {
				mprev.addEventListener('click', function () {
					track.scrollBy({ left: -track.clientWidth, behavior: 'smooth' });
				});
			}

			if (mnext) {
				mnext.addEventListener('click', function () {
					track.scrollBy({ left: track.clientWidth, behavior: 'smooth' });
				});
			}

			stav();
		}
	}

	/* --- Zvětšení fotky/videa a listování celou galerií ------------------ */

	const box = document.getElementById('lbox');

	if (!box) {
		return;
	}

	const velka = box.querySelector('img');
	const zavrit = box.querySelector('.lbclose');

	if (!velka || !zavrit) {
		return;
	}

	/*
	 * TASK AO DOPLNĚK (nález majitele 7. 9.): KANONICKÝ seznam položek
	 * galerie pro listování šipkami v lightboxu -- `.dgthumbs button`
	 * (video dlaždice, pak fotky, PŘESNĚ pořadí, ve kterém je vypisuje
	 * `game-hero-gallery.php`) je jediné místo v DOM, které pro každou
	 * položku už nese hotová data (`data-video-yt`/`data-video`/
	 * `data-full`) -- žádná druhá kopie dat, jen se čte znovu.
	 */
	const dgThumbs = gal ? Array.prototype.slice.call(gal.querySelectorAll('.dgthumbs button')) : [];

	let vratitFokus = null;
	let currentIndex = -1;

	/*
	 * TASK AO (nález majitele 7. 9.): inline kopie videa, kterou lightbox
	 * při otevření pozastaví (`.dgmain`/`.mgvideo` autoplay smyčka bez
	 * zvuku) -- při zavření lightboxu NEBO přechodu na jinou položku se
	 * přehraje dál, přesně odkud pozastavila. `null`, když lightbox
	 * otevřela fotka nebo YouTube fasáda (ta žádnou tichou inline kopii
	 * nemá, jen statický poster).
	 */
	let aktivniInlineVideo = null;

	/*
	 * Odstraní video vrstvu (`.lbvideo`, sdílené `components.css` pravidlo
	 * s `.rvideo`/HP showreel překryvem -- ř. 9854) a vrátí `<img>` zpět
	 * jako viditelný obsah lightboxu; OBNOVÍ i případnou pozastavenou
	 * inline smyčku. Volá se na začátku KAŽDÉHO otevření/přechodu (fotka
	 * i video), aby po sobě dvě po sobě jdoucí položky nenechaly cizí
	 * uzel navíc a odchod z video položky vždycky obnovil, co pozastavil.
	 */
	const zastavLbVideo = function () {
		const stary = box.querySelector('.lbvideo');

		if (stary) {
			box.removeChild(stary);
		}

		velka.hidden = false;

		if (aktivniInlineVideo) {
			const prehravani = aktivniInlineVideo.play();

			if (prehravani && 'function' === typeof prehravani.catch) {
				prehravani.catch(function () {
					/* Autoplay zamítnutý prohlížečem -- smyčka zůstane na pauze, žádná chyba v konzoli. */
				});
			}

			aktivniInlineVideo = null;
		}
	};

	const otevrit = function (src, alt, spoustec) {
		const bezpecny = safeSrc(src);

		if (!bezpecny) {
			return;
		}

		zastavLbVideo();
		velka.setAttribute('src', bezpecny);
		velka.setAttribute('alt', alt || '');
		box.hidden = false;
		box.classList.add('on');
		document.body.classList.add('lbox-open');
		vratitFokus = spoustec || null;
		zavrit.focus();
	};

	/*
	 * TASK AO (nález majitele 7. 9.): Play u video dlaždice hero galerie
	 * (dosud pozůstatek staré čistě fotkové galerie -- `.dgmain` klik
	 * vždycky otevřel lightbox s aktuálním `<img>` uvnitř, i když to byl
	 * jen poster YouTube fasády) teď přehraje SKUTEČNÉ video přímo
	 * v lightboxu, se zvukem. Dva zdrojové tvary (kontrakt `hero_video_kind`
	 * v `game-hero-gallery.php`): `ytId` (Spolupráce, YouTube) staví
	 * `youtube-nocookie.com` iframe -- STEJNÁ adresa a atributy jako
	 * `youtube-facade.js`/`hp2026.js` `initShowreel()` (žádná nová
	 * embed logika); `src` (detail hry, vlastní `.mp4`) staví `<video
	 * controls>` STEJNÝM vzorem jako `.dgal` modul (`lightbox.js`
	 * `showPhoto()`, `data-dgal-video`) -- na rozdíl od tiché inline
	 * smyčky NENÍ `muted`, takže hraje se zvukem (nález majitele).
	 * `.lbvideo`/`.lbvideo iframe` je existující CSS z HP showreel
	 * překryvu (`components.css` ř. 9854-9870) -- žádný nový blok.
	 */
	const otevritVideo = function (opts, spoustec) {
		if (!opts || (!opts.ytId && !opts.src)) {
			return;
		}

		/* Uvolní starou video vrstvu A obnoví PŘEDCHOZÍ pozastavenou inline smyčku (jiná položka). */
		zastavLbVideo();

		if (opts.src) {
			const main = gal ? gal.querySelector('.dgmain') : null;
			const inline = main ? main.querySelector('video') : null;

			if (inline) {
				const zdroj = inline.querySelector('source');
				const inlineSrc = safeSrc(zdroj ? zdroj.getAttribute('src') : inline.getAttribute('src'));

				/* Pozastaví tichou smyčku jen tehdy, když jde o TENTÝŽ soubor jako otevírané video (jinak by šlo o cizí přehrávač). */
				if (inlineSrc && inlineSrc === opts.src) {
					inline.pause();
					aktivniInlineVideo = inline;
				}
			}
		}

		const wrap = document.createElement('div');

		wrap.className = 'lbvideo';

		if (opts.ytId) {
			const frame = document.createElement('iframe');

			frame.setAttribute('src', 'https://www.youtube-nocookie.com/embed/' + opts.ytId + '?autoplay=1');
			frame.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture');
			frame.setAttribute('allowfullscreen', 'allowfullscreen');
			frame.setAttribute('frameborder', '0');

			if (opts.title) {
				frame.setAttribute('title', opts.title);
			}

			wrap.appendChild(frame);
		} else {
			const video = document.createElement('video');
			const zdroj = document.createElement('source');

			video.setAttribute('controls', 'controls');
			video.setAttribute('playsinline', 'playsinline');
			video.setAttribute('preload', 'metadata');
			video.autoplay = true;
			zdroj.setAttribute('src', opts.src);
			zdroj.setAttribute('type', 'video/mp4');
			video.appendChild(zdroj);
			wrap.appendChild(video);
		}

		velka.hidden = true;
		box.insertBefore(wrap, velka);
		box.hidden = false;
		box.classList.add('on');
		document.body.classList.add('lbox-open');
		vratitFokus = spoustec || null;
		zavrit.focus();

		/*
		 * Výslovné `.play()` NAD RÁMEC `autoplay` IDL vlastnosti výš --
		 * uzel se do dokumentu vkládá až tímhle voláním a spoléhat jen na
		 * `autoplay` by v některých prohlížečích znamenalo čekat na další
		 * cyklus. Volá se pořád uvnitř SYNCHRONNÍ reakce na klik/šipku/
		 * klávesu (uživatel gesto právě udělal), takže autoplay se zvukem
		 * prohlížeč nezakáže.
		 */
		const novyVideo = wrap.querySelector('video');

		if (novyVideo) {
			const prehravani = novyVideo.play();

			if (prehravani && 'function' === typeof prehravani.catch) {
				prehravani.catch(function () {
					/* Autoplay zamítnutý -- ovládací prvky videa zůstávají funkční. */
				});
			}
		}
	};

	const lbCount = box.querySelector('.lbcount');
	const lbPrev = box.querySelector('.lbnav.prev');
	const lbNext = box.querySelector('.lbnav.next');

	const aktualizujPocitadlo = function () {
		if (lbCount && dgThumbs.length > 0 && currentIndex >= 0) {
			lbCount.textContent = (currentIndex + 1) + ' / ' + dgThumbs.length;
		}
	};

	/**
	 * Vytáhne z `.dgthumbs button` popis položky pro `otevrit()`/`otevritVideo()`.
	 * Táž kontrola tvaru YouTube ID jako `youtube-embed.php`/`youtube-facade.js`.
	 *
	 * @param {Element} thumb Dlaždice filmstripu.
	 * @returns {object} Popis položky.
	 */
	const polozkaZThumbu = function (thumb) {
		const ytId = thumb.getAttribute('data-video-yt') || '';
		const ID_RX = /^[A-Za-z0-9_-]{11}$/;

		if (ytId && ID_RX.test(ytId)) {
			return {
				video: true,
				ytId: ytId,
				title: thumb.getAttribute('data-video-label') || '',
			};
		}

		const videoSrc = safeSrc(thumb.getAttribute('data-video'));

		if (videoSrc) {
			return { video: true, src: videoSrc };
		}

		const nahled = thumb.querySelector('img');

		return {
			video: false,
			src: safeSrc(thumb.getAttribute('data-full')),
			alt: nahled ? (nahled.getAttribute('alt') || '') : '',
		};
	};

	/*
	 * TASK AO DOPLNĚK (nález majitele 7. 9.): CYKLICKÉ listování -- `next`
	 * za poslední položkou se vrátí na první a naopak (modulo, ne ořez),
	 * přesně jak žádal nález. Jeden vstupní bod pro klik na `.dgmain`,
	 * klik na dlaždici mobilního pásu, šipky, klávesy i swipe -- žádná
	 * z těch pěti cest nemá vlastní kopii přepínací logiky.
	 */
	const otevriPolozku = function (index, spoustec) {
		const total = dgThumbs.length;

		if (0 === total) {
			return;
		}

		const norm = ( ( index % total ) + total ) % total;
		const polozka = polozkaZThumbu(dgThumbs[norm]);

		currentIndex = norm;

		if (polozka.video) {
			otevritVideo({ ytId: polozka.ytId, src: polozka.src, title: polozka.title }, spoustec);
		} else {
			otevrit(polozka.src, polozka.alt, spoustec);
		}

		aktualizujPocitadlo();
	};

	const zavri = function () {
		box.classList.remove('on');
		box.hidden = true;
		velka.setAttribute('src', '');
		zastavLbVideo();
		document.body.classList.remove('lbox-open');
		currentIndex = -1;

		if (vratitFokus && 'function' === typeof vratitFokus.focus) {
			vratitFokus.focus();
		}

		vratitFokus = null;
	};

	if (gal) {
		const main = gal.querySelector('.dgmain');

		if (main) {
			main.addEventListener('click', function (e) {
				/*
				 * `stopPropagation()` na fasádě -- BEZ NĚJ by delegovaný
				 * listener `youtube-facade.js` na `document` (stejná
				 * bublající fáze, spouští se AŽ PO tomhle) fasádu uvnitř
				 * `.dgmain` ROZBIL na přehrávající se iframe současně
				 * s otevřením lightboxu -- přesně nález majitele ("shodím
				 * lightbox, video už jede v okně"). Fasáda za lightboxem
				 * zůstává netknutá (poster + odkaz), video hraje jen
				 * v lightboxu.
				 */
				const facade = main.querySelector('.yt-facade');

				if (facade) {
					e.preventDefault();
					e.stopPropagation();
				}

				const aktivni = dgThumbs.filter(function (t) {
					return t.classList.contains('on');
				})[0];
				const index = aktivni ? dgThumbs.indexOf(aktivni) : 0;

				otevriPolozku(index, main);
			});
		}
	}

	/*
	 * TASK AO DOPLNĚK: mobilní swipe pás (`.mgtrack`) dřív klik na dlaždici
	 * vůbec nezachytával -- lightbox šel otevřít jen z desktopového
	 * `.dgmain`. Pořadí dětí `.mgtrack` je STEJNÉ jako `.dgthumbs button`
	 * (obě staví `game-hero-gallery.php` ze stejného pole -- video první,
	 * pak fotky), takže pozice v poli stačí jako index beze změny.
	 */
	if (mgal) {
		const track = mgal.querySelector('.mgtrack');

		if (track && dgThumbs.length > 0) {
			Array.prototype.slice.call(track.children).forEach(function (dite, i) {
				if (i >= dgThumbs.length) {
					return;
				}

				dite.addEventListener('click', function (e) {
					const facade = dite.querySelector('.yt-facade');

					if (facade) {
						e.preventDefault();
						e.stopPropagation();
					}

					otevriPolozku(i, dite);
				});
			});
		}
	}

	if (lbPrev) {
		lbPrev.addEventListener('click', function () {
			otevriPolozku(currentIndex - 1, vratitFokus || lbPrev);
		});
	}

	if (lbNext) {
		lbNext.addEventListener('click', function () {
			otevriPolozku(currentIndex + 1, vratitFokus || lbNext);
		});
	}

	zavrit.addEventListener('click', zavri);

	box.addEventListener('click', function (e) {
		if (e.target === box) {
			zavri();
		}
	});

	document.addEventListener('keydown', function (e) {
		if (!box.classList.contains('on')) {
			return;
		}

		if ('Escape' === e.key) {
			zavri();
		} else if ('ArrowRight' === e.key) {
			otevriPolozku(currentIndex + 1, vratitFokus);
		} else if ('ArrowLeft' === e.key) {
			otevriPolozku(currentIndex - 1, vratitFokus);
		}
	});

	/* Swipe na 390 (nález majitele): práh 40px, jeden prst, bez ovlivnění svislého scrollu (`passive: true`). */
	let dotykStartX = null;

	box.addEventListener('touchstart', function (e) {
		dotykStartX = (e.touches && 1 === e.touches.length) ? e.touches[0].clientX : null;
	}, { passive: true });

	box.addEventListener('touchend', function (e) {
		if (null === dotykStartX) {
			return;
		}

		const koncX = (e.changedTouches && e.changedTouches[0]) ? e.changedTouches[0].clientX : dotykStartX;
		const delta = koncX - dotykStartX;

		dotykStartX = null;

		if (Math.abs(delta) < 40) {
			return;
		}

		if (delta < 0) {
			otevriPolozku(currentIndex + 1, vratitFokus);
		} else {
			otevriPolozku(currentIndex - 1, vratitFokus);
		}
	}, { passive: true });
}

try {
	initGameGallery();
} catch (e) {
	console.error('TH2026 init selhal:', 'gameGallery', e);
}

/*
 * `lazy-preload` -- 1.27.13 zaverecna oprava dvojiteho tapu (viz docblok
 * `lazy-preload.js`): preloaduje obrazky karet/dlazdic driv, nez se do
 * nich doscrolluje, aby lazy nacteni nepadlo prave do okna tapu.
 */
try {
	initLazyPreload();
} catch (e) {
	console.error('TH2026 init selhal:', 'lazy-preload', e);
}

const reveal = { init: initReveal };
const hero = { init: initHero };
const carousel = { init: initCarousel };
const stats = { init: initStats };
const lightbox = { init: initLightbox };
const youtubeFacade = { init: initYoutubeFacade };
const roadmap = { init: initRoadmap };
const loksec = { init: initLoksec };
const formsec = { init: initFormsec };
const gamepick = { init: initGamepick };
const hp2026 = { init: initHp2026 };
const hpPartnerK13 = { init: initPartnerK13 };
const blogFilter = { init: initBlogFilter };
const blogLoadmore = { init: initBlogLoadmore };
const revmore = { init: initRevmore };
const locmapLazy = { init: initLocmapLazy };
const contactMapLazy = { init: initContactMapLazy };
const gameMapLazy = { init: initGameMapLazy };
const gameGallery = { init: initGameGallery };

const TH2026 = { setState, hasState, states, contracts, enforceInvariant, viewport, footer, reveal, hero, carousel, stats, lightbox, youtubeFacade, roadmap, loksec, formsec, gamepick, hp2026, hpPartnerK13, blogFilter, blogLoadmore, revmore, locmapLazy, contactMapLazy, gameMapLazy, gameGallery };

export { setState, hasState, states, contracts, enforceInvariant, viewport, footer, reveal, hero, carousel, stats, lightbox, youtubeFacade, roadmap, loksec, formsec, gamepick, hp2026, hpPartnerK13, blogFilter, blogLoadmore, revmore, locmapLazy, contactMapLazy, gameMapLazy, gameGallery };
export default TH2026;
